import { memo, useMemo, useCallback, useState, useEffect } from 'react'
import {
  Alert,
  Box,
  Button,
  capitalize,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Skeleton,
  Snackbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import axios from 'src/utilities/http-client'
import { downloadFile } from 'src/v3/utilities/filesystem'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import {
  Warning as WarningIcon,
  Badge as LicenseIcon,
  GetApp as DownloadIcon,
  Edit as EditIcon,
  Verified as VerifyIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Add as AddIcon,
} from '@mui/icons-material'
import { License, VerificationStatus } from 'src/v3/core/types'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import type { RosterCredentialItem } from '../types'
import { LicenseForm } from './modals/license-form'
import { VerifyLicense } from './modals/verify-license'
import {
  fetchLicenses,
  selectActiveLinkedService,
  selectLicenses,
  selectLicensesStatus,
} from '../../manage.slice'

dayjs.extend(utc)

interface LicensesDataGridProps {
  licensesRows: RosterCredentialItem[]
  selectedUserLicensesCredentialsStatus: string
  total: number
  expiredLicenses: number
  expiringSoonLicenses: number
  userID: string
  verificationEnabled: boolean
  licenseVerificationStatuses?: Record<string, VerificationStatus>
}

export const LicensesDataGrid = memo(
  ({
    licensesRows,
    selectedUserLicensesCredentialsStatus,
    total,
    expiredLicenses,
    expiringSoonLicenses,
    userID,
    verificationEnabled,
    licenseVerificationStatuses,
  }: LicensesDataGridProps) => {
    let dispatch = useAppDispatch()

    let linkedServiceID = useAppSelector(selectActiveLinkedService)
    let licenses = useAppSelector(selectLicenses)
    let licensesStatus = useAppSelector(selectLicensesStatus)

    let rowsWithVerification = !verificationEnabled
      ? licensesRows
      : licensesRows.map((r) => ({
          ...r,
          verificationStatus: licenseVerificationStatuses?.[r.id] || 'pending',
        }))

    useEffect(() => {
      if (
        !licensesStatus[linkedServiceID] ||
        licensesStatus[linkedServiceID] === 'uninitialized'
      ) {
        dispatch(fetchLicenses(linkedServiceID))
      }
    }, [licensesStatus, linkedServiceID])

    // Track downloading license IDs (Set allows multiple simultaneous downloads)
    let [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set())

    // Track edit modal state
    let [editModalLicense, setEditModalLicense] = useState<License>()

    let [verifyLicense, setVerifyLicense] =
      useState<RosterCredentialItem | null>(null)

    // Track view state
    let [viewBlob, setViewBlob] = useState<Blob | null>(null)
    let [viewingId, setViewingId] = useState<string>('')
    let [viewedLicense, setViewedLicense] =
      useState<RosterCredentialItem | null>(null)

    // Track snackbar state
    let [snackbar, setSnackbar] = useState<{
      isOpen: boolean
      message: string
      severity: 'success' | 'error'
    }>({
      isOpen: false,
      message: '',
      severity: 'success',
    })

    // Extension mapping for blob types
    let extensions: Record<string, string> = {
      'application/pdf': 'pdf',
      'image/jpeg': 'jpg',
      'image/png': 'png',
    }

    // Handle license download
    let handleDownload = useCallback(
      async (row: RosterCredentialItem) => {
        if (!row.fileUrl) return

        setDownloadingIds((prev) => new Set(prev).add(row.id))

        try {
          let fileResponse = await axios.get<Blob>(row.fileUrl, {
            responseType: 'blob',
          })
          let blob = fileResponse.data
          let extension = extensions[blob.type] || 'pdf'
          let filename = `${row.category.replace(/\s+/g, '_')}_${row.type.replace(/\s+/g, '_')}_${row.number.replace(/\s+/g, '_')}.${extension}`

          await downloadFile(blob, filename)
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error downloading license:', err)
        } finally {
          setDownloadingIds((prev) => {
            let next = new Set(prev)
            next.delete(row.id)
            return next
          })
        }
      },
      [extensions],
    )

    // Handle license view
    let handleView = useCallback(async (row: RosterCredentialItem) => {
      if (!row.fileUrl) return

      setViewingId(row.id)

      try {
        // Fetch the blob directly from fileUrl
        let fileResponse = await axios.get<Blob>(row.fileUrl, {
          responseType: 'blob',
        })

        setViewBlob(fileResponse.data)
        setViewedLicense(row)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error viewing license:', err)
      } finally {
        setViewingId('')
      }
    }, [])

    // Memoized date formatters to avoid repeated dayjs parsing on every render
    let formatIssueDate = useCallback((value: Date | undefined) => {
      if (!value) return 'N/A'
      return dayjs(value).utc().format('MMM D, YYYY')
    }, [])

    let formatExpirationDate = useCallback((value: Date | undefined) => {
      if (!value) return 'N/A'
      return dayjs(value).utc().format('MMM D, YYYY')
    }, [])

    let formatDateAdded = useCallback(
      (value: Date | undefined) =>
        value ? dayjs(value).format('MMM D, YYYY') : '',
      [],
    )

    // Stable reference to check if license is downloading
    let isLicenseDownloading = useCallback(
      (id: string) => downloadingIds.has(id),
      [downloadingIds],
    )

    // Licenses DataGrid columns (excluding userName and email for user details)
    let licensesColumns = useMemo<GridColDef[]>(
      () => [
        {
          field: 'type',
          headerName: 'Type',
          width: 90,
        },
        {
          field: 'number',
          headerName: 'Number',
          width: 150,
        },
        {
          field: 'state',
          headerName: 'State',
          minWidth: 150,
          renderCell: (params) =>
            params.value || (
              <Box className="flex items-center justify-center h-full">
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              </Box>
            ),
        },
        {
          field: 'actions',
          headerName: 'Actions',
          flex: 0,
          minWidth: 150,
          sortable: false,
          filterable: false,
          disableExport: true,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params) => {
            let row = params.row as RosterCredentialItem
            let isDownloading = isLicenseDownloading(row.id)

            let { verificationStatus } = row
            let verifyDisabled =
              !!verificationStatus && verificationStatus !== 'pending'
            let verifyTooltip = verifyDisabled
              ? verificationStatus === 'accepted'
                ? 'License has been accepted'
                : verificationStatus === 'rejected'
                  ? 'License has been rejected'
                  : 'Update Verification Status'
              : 'Update Verification Status'
            let showView = true
            let hasFile = !!row.fileUrl
            let isPdf = row.fileUrl?.includes('pdf')
            let isViewing = viewingId === row.id
            let viewDisabled = !hasFile || isPdf || isViewing
            let viewTooltip = !hasFile
              ? 'File not found'
              : isPdf
                ? 'Cannot preview PDF files. Download the file to view.'
                : 'View'

            return (
              <Box className="flex items-center justify-start h-full gap-1">
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setEditModalLicense(
                        licenses[linkedServiceID]?.find((l) => l.id === row.id),
                      )
                    }
                    aria-label="Edit license"
                    sx={{ color: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {showView && (
                  <Tooltip title={viewTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleView(row)}
                        disabled={viewDisabled}
                        loading={isViewing}
                        aria-label={viewTooltip}
                        sx={{ color: 'primary.main' }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title={!hasFile ? 'File not found' : 'Download'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(row)}
                      disabled={!hasFile || isDownloading}
                      loading={isDownloading}
                      aria-label={
                        isDownloading
                          ? 'Downloading license'
                          : 'Download license'
                      }
                      sx={{ color: 'primary.main' }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                {verificationEnabled && (
                  <Tooltip title={verifyTooltip}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => setVerifyLicense(row)}
                        disabled={verifyDisabled}
                        aria-label={verifyTooltip}
                        sx={{ color: 'primary.main' }}
                      >
                        <VerifyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )
          },
        },
        {
          field: 'verificationStatus',
          headerName: 'Verification',
          width: 120,
          sortable: false,
          align: 'left',
          headerAlign: 'left',
          renderCell: (params) => {
            let row = params.row as RosterCredentialItem

            if (verificationEnabled) {
              let { verificationStatus } = row
              let statusColors = {
                accepted: 'success.main',
                rejected: 'error.main',
                pending: 'warning.main',
              }
              return (
                <Box className="flex items-center justify-start h-full">
                  <Typography
                    variant="body2"
                    sx={{
                      color: verificationStatus
                        ? statusColors[verificationStatus]
                        : 'text.secondary',
                      fontWeight: 500,
                    }}
                  >
                    {verificationStatus ? capitalize(verificationStatus) : '-'}
                  </Typography>
                </Box>
              )
            }

            return (
              <Box className="flex items-center justify-center h-full">
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              </Box>
            )
          },
        },
        {
          field: 'issueDate',
          headerName: 'Issue Date',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) => formatIssueDate(value),
        },
        {
          field: 'expiration',
          headerName: 'Expiration',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) =>
            formatExpirationDate(value),
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 130,
          sortComparator: (v1: string, v2: string) => {
            let priority: Record<string, number> = {
              'expiring-soon': 1,
              active: 2,
              expired: 3,
            }
            let p1 = priority[v1] ?? 4
            let p2 = priority[v2] ?? 4
            return p1 - p2
          },
          renderCell: (params) => {
            let color: 'success' | 'warning' | 'error' = (() => {
              switch (params.value) {
                case 'expired':
                  return 'error'
                case 'expiring-soon':
                  return 'warning'
                case 'active':
                  return 'success'
                default:
                  return 'success'
              }
            })()

            let icon = (() => {
              switch (params.value) {
                case 'expired':
                  return <ErrorIcon fontSize="small" />
                case 'expiring-soon':
                  return <WarningIcon fontSize="small" />
                case 'active':
                  return <CheckCircleIcon fontSize="small" />
                default:
                  return <CheckCircleIcon fontSize="small" />
              }
            })()

            return (
              <Chip
                label={params.value.replace('-', ' ')}
                icon={icon}
                color={color}
                size="medium"
                variant="outlined"
                sx={{ textTransform: 'capitalize' }}
                aria-label={`Status: ${params.value.replace('-', ' ')}`}
              />
            )
          },
        },
        {
          field: 'daysToExpiration',
          headerName: 'Days to Expiration',
          width: 150,
          renderCell: (params) => {
            if (params.value === undefined || params.value === null)
              return 'N/A'

            if (params.value < 0) {
              return `${Math.abs(params.value)} days expired`
            }

            return `${params.value} days remaining`
          },
        },
        {
          field: 'dateAdded',
          headerName: 'Date Added',
          width: 120,
          type: 'date',
          valueGetter: (value: string | null) =>
            value ? dayjs(value).toDate() : undefined,
          valueFormatter: (value: Date | undefined) => formatDateAdded(value),
        },
      ],
      [
        isLicenseDownloading,
        handleDownload,
        handleView,
        viewingId,
        verificationEnabled,
        formatIssueDate,
        formatExpirationDate,
        formatDateAdded,
      ],
    )

    // Custom licenses toolbar
    let LicensesToolbar = useCallback(
      () => <DataGridToolbar fileName="user-licenses" />,
      [],
    )

    return (
      <Box>
        <Box className="px-6 mb-6">
          <Box className="flex items-center justify-between mb-2">
            <Typography
              variant="h6"
              className="flex items-center gap-2 pr-2 font-semibold"
              id="licenses-section-heading"
            >
              <LicenseIcon aria-hidden="true" />
              Licenses & Certifications
            </Typography>
            <Box className="flex items-center gap-2">
              {total > 0 && (
                <>
                  <Chip
                    label={`${total} Total`}
                    size="medium"
                    variant="outlined"
                    color="primary"
                  />
                  {expiredLicenses > 0 && (
                    <Chip
                      label={`${expiredLicenses} Expired`}
                      size="medium"
                      variant="outlined"
                      color="error"
                    />
                  )}
                  {expiringSoonLicenses > 0 && (
                    <Chip
                      label={`${expiringSoonLicenses} Expiring Soon`}
                      size="medium"
                      variant="outlined"
                      color="warning"
                    />
                  )}
                </>
              )}
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => setEditModalLicense({ id: 'create' } as any)}
                aria-label="Create new license"
              >
                Create License
              </Button>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary">
            All licenses and certifications for this user. Use the toolbar to
            filter, search, and export to CSV format.
          </Typography>
        </Box>
        {selectedUserLicensesCredentialsStatus === 'rejected' ? (
          <Box
            className="grid py-12 place-items-center"
            role="alert"
            aria-labelledby="licenses-section-heading"
          >
            <Box className="text-center" sx={{ color: 'text.secondary' }}>
              <WarningIcon
                className="mb-4 opacity-50"
                sx={{
                  fontSize: 64,
                  color: 'warning.main',
                }}
                aria-hidden="true"
              />
              <Typography variant="h6" className="mb-2">
                Failed to Load Licenses
              </Typography>
              <Typography variant="body2">
                There was an error loading the licenses data. Please try
                refreshing the page.
              </Typography>
            </Box>
          </Box>
        ) : licensesRows.length === 0 ? (
          <Box
            className="grid py-12 place-items-center"
            role="status"
            aria-labelledby="licenses-section-heading"
          >
            <Box className="text-center" sx={{ color: 'text.secondary' }}>
              <LicenseIcon
                className="mb-4 opacity-50"
                sx={{ fontSize: 64 }}
                aria-hidden="true"
              />
              <Typography variant="h6" className="mb-2">
                No Licenses or Certifications Found
              </Typography>
              <Typography variant="body2">
                No licenses found for this user.
              </Typography>
            </Box>
          </Box>
        ) : selectedUserLicensesCredentialsStatus === 'pending' ? (
          <Skeleton
            variant="rectangular"
            height={500}
            sx={{ borderRadius: 1 }}
            aria-label="Loading licenses data"
          />
        ) : (
          <Box
            className="w-full h-full"
            sx={{ height: 500 }}
            role="region"
            aria-labelledby="licenses-section-heading"
          >
            <DataGridPremium
              rows={rowsWithVerification}
              columns={licensesColumns}
              slots={{ toolbar: LicensesToolbar }}
              showToolbar
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
                pinnedColumns: { right: ['actions'] },
              }}
              pageSizeOptions={[10, 25, 50]}
              checkboxSelection
              disableRowSelectionOnClick
              aria-label="User licenses and certifications"
              getRowId={(row) => (row as any).id}
              sx={{
                backgroundColor: 'background.paper',
                border: 1,
                borderRadius: 1,
                borderColor: 'divider',
                '& .MuiDataGrid-toolbarContainer': {
                  borderBottom: 1,
                  borderColor: 'divider',
                  p: 2,
                  backgroundColor: 'background.default',
                },
                '& .MuiDataGrid-cell': {
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'background.paperDark',
                  borderColor: 'divider',
                },
                '& .MuiDataGrid-footerContainer': {
                  borderColor: 'divider',
                  backgroundColor: 'background.paperDark',
                },
                '& .MuiDataGrid-row': {
                  '&:hover': {
                    backgroundColor: 'background.default',
                  },
                },
              }}
            />
          </Box>
        )}
        {/* Edit License Modal */}
        <LicenseForm
          open={!!editModalLicense}
          setOpen={() => setEditModalLicense(undefined)}
          license={licenses[linkedServiceID]?.find(
            (l) => l.id === editModalLicense?.id,
          )}
          userID={userID}
          onError={() => {}}
        />
        {/* Verify License Modal */}
        <VerifyLicense
          open={!!verifyLicense}
          setOpen={() => setVerifyLicense(null)}
          licenseID={verifyLicense?.id || ''}
          linkedServiceID={linkedServiceID}
          userID={userID}
        />
        {/* View License Dialog */}
        <Dialog
          open={!!viewBlob}
          onClose={() => {
            setViewBlob(null)
            setViewedLicense(null)
          }}
          maxWidth="lg"
        >
          <DialogContent>
            {viewBlob && viewedLicense && (
              <img
                src={URL.createObjectURL(viewBlob)}
                alt={`${viewedLicense.type} license`}
              />
            )}
          </DialogContent>
        </Dialog>
        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.isOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, isOpen: false })}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    )
  },
)

export default LicensesDataGrid
